import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  createChannelPartnerLead,
  listChannelPartners,
} from "../../services/channelPartnerService";
import { listTenantOrganizations } from "../../services/organizationService";
import { getProjectDetails } from "../Project/components/ShowAll/ShowAllService";
import { getStage } from "../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import {
  selectActiveOrganizationId,
  selectIsSuperAdmin,
  selectRole1,
} from "../../redux/authSlice";
import urls from "../../global/constants/UrlConstants";
import YhButton from "../../ui/YhButton";
import YhInput from "../../ui/YhInput";
import YhSelect, { buildStageSelectGroups } from "../../ui/YhSelect";
import YhMultiSelect from "../../ui/YhMultiSelect";
import PhoneWithCountryCode, {
  composePhoneWithCountry,
  defaultPhoneCountry,
  findPhoneCountry,
  nationalPhoneDigits,
} from "../../Shared/Phone/PhoneWithCountryCode";

const emptyLeadForm = () => ({
  channelPartnerId: "",
  leadName: "",
  leadPhone: "",
  phoneCountryCode: defaultPhoneCountry().code,
  leadEmail: "",
  organizationIds: [] as string[],
  projectIds: [] as string[],
  stage: "",
});

function firstStageValue(
  groups: { options: { value: string | number }[] }[]
) {
  for (const g of groups || []) {
    if (g?.options?.[0]?.value != null && g.options[0].value !== "") {
      return String(g.options[0].value);
    }
  }
  return "Lead";
}

const ChannelPartnerAddLeadPage: React.FC = () => {
  const history = useHistory();
  const activeOrgId = useSelector(selectActiveOrganizationId);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const hierarchyLevel = useSelector(selectRole1);
  const canSelectOrganization =
    isSuperAdmin || String(hierarchyLevel) === "1";

  const lockedOrgId =
    !canSelectOrganization &&
    activeOrgId &&
    activeOrgId !== "all" &&
    activeOrgId !== "*"
      ? String(activeOrgId)
      : "";

  const scopedOrgId =
    lockedOrgId ||
    (activeOrgId && activeOrgId !== "all" && activeOrgId !== "*"
      ? String(activeOrgId)
      : "");

  const [partners, setPartners] = useState<any[]>([]);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [stageData, setStageData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyLeadForm());

  const stageGroups = useMemo(
    () => buildStageSelectGroups(stageData || []),
    [stageData]
  );

  const selectedOrgIds = useMemo(() => {
    if (scopedOrgId) return [scopedOrgId];
    return form.organizationIds || [];
  }, [scopedOrgId, form.organizationIds]);

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listChannelPartners({
        organizationId: scopedOrgId || undefined,
        page: 1,
        limit: 500,
      });
      const data = res?.data || res;
      setPartners(Array.isArray(data?.items) ? data.items : []);
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load Channel Partners");
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }, [scopedOrgId]);

  const loadProjects = useCallback(async () => {
    try {
      const res = await getProjectDetails({
        pageNumber: 0,
        pageSize: 500,
        search: "",
        status: "1",
        propertyPurpose: "",
      });
      setProjects(Array.isArray(res?.data) ? res.data : []);
    } catch (_) {
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    loadPartners();
    loadProjects();
  }, [loadPartners, loadProjects]);

  useEffect(() => {
    (async () => {
      try {
        const res = await listTenantOrganizations();
        const list = res?.data?.items || res?.data || res?.items || [];
        setOrgs(Array.isArray(list) ? list : []);
      } catch (_) {
        setOrgs([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getStage();
        const list = Array.isArray(res?.data) ? res.data : [];
        setStageData(list);
        const groups = buildStageSelectGroups(list);
        setForm((f) => ({
          ...f,
          stage: f.stage || firstStageValue(groups),
        }));
      } catch (_) {
        setStageData([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (scopedOrgId) {
      setForm((f) => ({ ...f, organizationIds: [scopedOrgId] }));
    }
  }, [scopedOrgId]);

  const selectedPartner = useMemo(
    () =>
      partners.find((p) => String(p._id) === String(form.channelPartnerId)) ||
      null,
    [partners, form.channelPartnerId]
  );

  const partnerOptions = useMemo(
    () => [
      {
        label: loading ? "Loading partners…" : "Select channel partner…",
        value: "",
      },
      ...partners.map((p) => ({
        label: `${p.firmName || "Partner"}${p.phone ? ` · ${p.phone}` : ""}`,
        value: String(p._id),
      })),
    ],
    [partners, loading]
  );

  const orgOptions = useMemo(() => {
    const mems = selectedPartner?.memberships || [];
    if (mems.length) {
      return mems.map((m: any) => ({
        label: m.organizationName || String(m.organizationId),
        value: String(m.organizationId),
      }));
    }
    return orgs.map((o) => ({
      label: o.brandName || o.name,
      value: String(o._id),
    }));
  }, [selectedPartner, orgs]);

  const projectOptions = useMemo(() => {
    const orgSet = new Set(selectedOrgIds.map(String).filter(Boolean));
    const mems = selectedPartner?.memberships || [];
    return (projects || [])
      .filter((p: any) => {
        if (!orgSet.size) return false;
        const oid = String(p.organizationId || "");
        if (oid && !orgSet.has(oid)) return false;
        if (!mems.length) return true;
        const mem = mems.find((m: any) => String(m.organizationId) === oid);
        if (!mem) return !oid;
        const pids = Array.isArray(mem.projectIds) ? mem.projectIds : [];
        if (!pids.length) return true;
        return pids.map(String).includes(String(p._id));
      })
      .map((p: any) => ({
        label: p.project_name || p.name || p.project_uid || String(p._id),
        value: String(p._id),
      }));
  }, [projects, selectedOrgIds, selectedPartner]);

  useEffect(() => {
    const allowed = new Set(projectOptions.map((p) => String(p.value)));
    setForm((f) => {
      const next = (f.projectIds || []).filter((id) => allowed.has(String(id)));
      if (next.length === (f.projectIds || []).length) return f;
      return { ...f, projectIds: next };
    });
  }, [projectOptions]);

  const onPartnerChange = (value: string) => {
    const partner =
      partners.find((p) => String(p._id) === String(value)) || null;
    const mems = partner?.memberships || [];
    const defaultOrgs = scopedOrgId
      ? [scopedOrgId]
      : mems.length === 1
        ? [String(mems[0].organizationId)]
        : [];
    setForm((f) => ({
      ...f,
      channelPartnerId: value,
      organizationIds: defaultOrgs,
      projectIds: [],
    }));
  };

  const submit = async () => {
    if (!form.channelPartnerId) {
      openErrorNotification("Select a Channel Partner");
      return;
    }
    const organizationIds = scopedOrgId
      ? [scopedOrgId]
      : form.organizationIds || [];
    if (!organizationIds.length) {
      openErrorNotification("Select at least one organization");
      return;
    }
    if (!form.leadName.trim()) {
      openErrorNotification("Lead name is required");
      return;
    }
    const national = nationalPhoneDigits(form.leadPhone, 15);
    if (
      !(
        national.length === 4 ||
        (national.length >= 7 && national.length <= 15)
      )
    ) {
      openErrorNotification(
        "Enter a valid mobile (10 digits) or last 4 digits"
      );
      return;
    }
    if (!form.stage) {
      openErrorNotification("Select a stage");
      return;
    }
    const country = findPhoneCountry(form.phoneCountryCode) || defaultPhoneCountry();
    const composed = composePhoneWithCountry(country.dialCode, national);
    setSaving(true);
    try {
      const res = await createChannelPartnerLead(form.channelPartnerId, {
        leadName: form.leadName.trim(),
        leadPhone: composed,
        phoneDialCode: country.dialCode,
        phoneCountryCode: country.code,
        leadEmail: form.leadEmail.trim() || undefined,
        organizationIds,
        projectIds: form.projectIds || [],
        stage: form.stage,
      });
      if (res?.success === 0 || !res?.data) {
        openErrorNotification(res?.message || "Failed to add lead");
        return;
      }
      const referenced = Number(res?.data?.referencedCount || 0);
      const count = Number(res?.data?.createdCount || 1);
      openSuccessNotification(
        referenced > 0 && referenced >= count
          ? "Existing lead linked to this Channel Partner (reference)."
          : referenced > 0
            ? `Lead saved — ${referenced} existing lead(s) linked as reference.`
            : count > 1
              ? `Lead added for ${count} organizations.`
              : "Lead added for Channel Partner."
      );
      if (res?.data?.errors?.length) {
        openErrorNotification(res.data.errors.join("; "));
      }
      setForm((f) => ({
        ...emptyLeadForm(),
        channelPartnerId: f.channelPartnerId,
        organizationIds: scopedOrgId
          ? [scopedOrgId]
          : f.organizationIds || [],
        stage: f.stage || firstStageValue(stageGroups),
      }));
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to add lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="box-border flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white">
      <div className="shrink-0 border-b border-[#EEF0F5] bg-white px-4 py-4 md:px-5">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
          Add Channel Partner Lead
        </h1>
        <p className="mt-1 text-sm text-[#6B7280]">
          Select one or more organizations and projects. Enter mobile with
          country code (full 10 digits or last 4). Source is set to Channel
          Partner automatically.
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-4 py-4 md:px-5">
        <div className="mb-4 border-b border-[#EEF0F5] pb-3">
          <h2 className="text-sm font-semibold text-[#111827]">Lead details</h2>
        </div>

        <div className="grid w-full grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2 xl:grid-cols-3">
          <YhSelect
            label="Channel Partner *"
            value={form.channelPartnerId}
            onChange={(e: any) =>
              onPartnerChange(String(e?.target?.value || ""))
            }
            options={partnerOptions}
            disabled={loading}
          />

          {scopedOrgId ? (
            <div className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-3 text-sm text-[#374151]">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#6B7280]">
                Organization
              </div>
              Using organization from header
            </div>
          ) : (
            <YhMultiSelect
              label="Organizations"
              placeholder={
                form.channelPartnerId
                  ? "Select organizations…"
                  : "Select channel partner first…"
              }
              searchPlaceholder="Search organizations…"
              requiredMark
              showSelectAll
              maxVisibleChips={4}
              disabled={!form.channelPartnerId}
              options={orgOptions}
              value={form.organizationIds}
              onChange={(values) =>
                setForm((f) => ({
                  ...f,
                  organizationIds: values.map(String),
                  projectIds: [],
                }))
              }
            />
          )}

          <YhMultiSelect
            label="Projects"
            placeholder={
              selectedOrgIds.length
                ? "Select projects…"
                : "Select organization first…"
            }
            searchPlaceholder="Search projects…"
            showSelectAll
            maxVisibleChips={4}
            disabled={!selectedOrgIds.length}
            options={projectOptions}
            value={form.projectIds}
            onChange={(values) =>
              setForm((f) => ({
                ...f,
                projectIds: values.map(String),
              }))
            }
          />

          <YhInput
            label="Lead name *"
            value={form.leadName}
            onChange={(e: any) =>
              setForm((f) => ({ ...f, leadName: e.target.value }))
            }
            placeholder="Customer name"
          />
          <PhoneWithCountryCode
            label="Mobile"
            required
            countryCode={form.phoneCountryCode}
            phone={form.leadPhone}
            phonePlaceholder="10-digit or last 4"
            onCountryChange={(code) =>
              setForm((f) => ({ ...f, phoneCountryCode: code }))
            }
            onPhoneChange={(digits) =>
              setForm((f) => ({ ...f, leadPhone: digits }))
            }
          />
          <YhInput
            label="Email"
            value={form.leadEmail}
            onChange={(e: any) =>
              setForm((f) => ({ ...f, leadEmail: e.target.value }))
            }
            placeholder="optional"
          />
          <YhSelect
            label="Stage *"
            placeholder="Select stage"
            searchPlaceholder="Search stages..."
            searchable
            showOptionIcon={false}
            value={form.stage}
            groups={stageGroups}
            onChange={(e: any) =>
              setForm((f) => ({
                ...f,
                stage: String(e?.target?.value || ""),
              }))
            }
          />
        </div>
      </div>

      <div className="shrink-0 border-t border-[#EEF0F5] bg-white px-4 py-3 md:px-5">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <YhButton
            variant="secondary"
            size="sm"
            disabled={saving}
            onClick={() => history.push(urls.channelPartnersPath)}
          >
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            loading={saving}
            onClick={submit}
          >
            Save lead
          </YhButton>
        </div>
      </div>
    </div>
  );
};

export default ChannelPartnerAddLeadPage;
